// Management-plane access to routers and switches (Domain 06): remote SSH /
// Telnet logins from Windows endpoints and AAA test requests. The decision is
// derived from live state: reachability and interface ACLs (sim.js), the VTY
// line (transport input, access-class), SSH keys/version, and the login method
// (line password, local users or an AAA method list with RADIUS/TACACS+).

import { roundTrip, mgmtReach, dev } from './sim.js';
import { mgmtOf, evalAcl, authenticate, accountExec, aaaRequest, loginPrompt } from './security.js';
import { logEvent, clockLine } from './services.js';

const PORT = { ssh: 22, telnet: 23 };
/** deps for security.authenticate: reachability of an AAA server from the device. */
export const aaaDeps = {
  reach: (net, id, ip, o) => {
    const a = mgmtOf(net.devices[id]).aaa;
    return mgmtReach(net, id, ip, { ...o, srcIf: a?.srcIf?.[o.port === 49 ? 'tacacs' : 'radius'] || null });
  },
};

/**
 * Step 1: open the TCP connection and pass the VTY checks.
 * Returns { stage: 'ok'|'timeout'|'unreachable'|'refused'|'reset'|'version', id, banner, prompt, unreachableFrom }.
 */
export function remoteConnect(net, fromId, ip, proto) {
  const port = PORT[proto];
  const rt = roundTrip(net, fromId, ip, { proto: 'tcp', port });
  if (!rt.ok) {
    if (rt.stage === 'refused') return { stage: 'refused' };
    if (rt.unreachableFrom) return { stage: 'unreachable', unreachableFrom: rt.unreachableFrom };
    return { stage: 'timeout' };
  }
  const d = rt.target;
  const m = mgmtOf(d);
  const src = rt.fwd.pkt.src;
  if (m.lines.vty.accessClass) {
    const r = evalAcl(d, m.lines.vty.accessClass, { src, dst: ip, proto: 'tcp', dport: port, sport: 50000 }, { count: true });
    if (!r.permit) return { stage: proto === 'ssh' ? 'reset' : 'refused', id: d.id };
  }
  if (proto === 'ssh' && m.ssh.version === 1) return { stage: 'version', id: d.id };
  return { stage: 'ok', id: d.id, src, banner: m.banner, prompt: loginPrompt(d, 'vty'), noPassword: !m.aaa?.newModel && m.lines.vty.login === 'line' && !m.lines.vty.password };
}

/** Step 2: authenticate on the VTY line. Logs and AAA accounting as the device would. */
export function remoteLogin(net, id, { user, pass, src, proto }) {
  const d = dev(net, id);
  const r = authenticate(net, id, { line: 'vty', user, pass, src, record: true }, aaaDeps);
  const m = mgmtOf(d);
  const when = clockLine(net, id).replace(/^\*/, '').replace(/\.\d+ /, ' ');
  if (r.ok && m.loginLog.success) logEvent(net, id, `%SEC_LOGIN-5-LOGIN_SUCCESS: Login Success [user: ${user || ''}] [Source: ${src}] [localport: ${PORT[proto]}] at ${when}`);
  if (!r.ok && m.loginLog.failure) logEvent(net, id, `%SEC_LOGIN-4-LOGIN_FAILED: Login failed [user: ${user || ''}] [Source: ${src}] [localport: ${PORT[proto]}] [Reason: Login Authentication Failed] at ${when}`);
  if (r.ok) accountExec(net, id, user, src, aaaDeps);
  return { ...r, hostname: d.hostname, priv: r.priv ?? 1 };
}

/** IOS `test aaa group <radius|tacacs+> user pass legacy` against every configured server in order. */
export function testAaa(net, id, proto, user, pass) {
  const d = dev(net, id);
  const a = mgmtOf(d).aaa;
  const servers = Object.entries(a?.servers?.[proto] || {});
  if (!a?.newModel || !servers.length) return { result: 'none' };
  for (const [, srv] of servers) {
    const r = aaaRequest(net, id, proto, srv, user, pass, aaaDeps);
    if (r.result !== 'timeout') return r;
  }
  return { result: 'timeout' };
}
